// Saving a run, in the browser.
//
// A **snapshot** of the state is the save of record; the log rides beside it.
//
// The log is this project's save format everywhere else, and for good reasons
// — it is also the undo, the replay, the telemetry substrate and the network
// protocol. It is the wrong choice *here*, for one that outweighs them: a log
// only means anything against the rules that produced it, so any change to the
// rules invalidates every run in every player's browser. A prototype that is
// deployed several times a day would throw away people's games as a matter of
// routine, and "your save is gone because we tuned a drop rate" is not a
// trade worth making. A snapshot survives everything except a change to the
// *shape* of the state.
//
// The usual objection to snapshots is that a serialiser is a second copy of
// the shape and drifts from it. That is answered in `codec.ts`, which is
// structural rather than schema-driven — there is no field list to forget —
// and by a round-trip test that compares `hashState`, which covers every field
// the game actually reads.
//
// **The raw log is saved beside the snapshot, and verified before it is
// believed** (§29 U5). Undo and the history preview are log operations, so a
// reload without one lands a run you cannot look back over or take anything
// back in. It is written as a hint and never as an authority: on Continue it is
// replayed from `newGame()` and its final hash compared with the snapshot's —
// match and history survives the reload, mismatch and the snapshot loads alone.
// So a rules change still costs nobody their run, which is the whole trade
// above; it costs them only the history of it, said once.
//
// **Nothing about a run may be derived from the log.** The log is here to view
// and to undo. Everything else — the round, the difficulty, the clock — is read
// off the snapshot and the wrapper, as it was.
//
// The stored shape, the version stamp and that check are `saveFormat.ts`; this
// file is reading, writing and the run clock.

import { decode, encode } from "./codec";
import { CARD_ALIASES } from "src/engine/rules/campaign/cards";
import { hashState } from "src/engine/rules/hash";
import { rebuildStructureMirror } from "src/engine/rules/industry/structures";
import { syncArtifactMirror } from "src/engine/rules/industry/artifactMirror";
import { restorePieceIds } from "src/engine/rules/position";
import type { Level } from "src/engine/ai/difficulty";
import type { Command, GameState } from "src/engine/rules/commands";
import { FORMAT, migrate, selfContained, type StoredRun, verifies } from "./saveFormat";

const KEY = "factory-chess:run";

/** What the menu shows on the Continue card, or null when there is no save. */
export interface SaveSummary {
  level: string;
  plies: number;
  difficulty?: Level;
  saved: number;
  /** Total time in this run. See `StoredRun.played`. */
  played?: number;
}

/**
 * The longest gap between two saves that still counts as playing.
 *
 * **Time spent in a run is not the same as time since the run was opened**, and
 * the difference is a tab left open overnight. A save is written on every
 * command — the opponent's turns and the run director's included — so during
 * play the gap between two is seconds, and the only way to produce a large one
 * is to stop playing. Capping each step means a laptop shut for eight hours
 * adds five minutes rather than eight hours.
 *
 * Generous enough not to punish thinking: five minutes is longer than anybody
 * spends on a move, so a real decision is counted in full.
 */
const IDLE_MS = 5 * 60 * 1000;

/**
 * The run clock, accumulated between saves.
 *
 * Module state, which `src/engine/rules/` forbids and this is not: it is a *display*
 * total that no rule reads, nothing derives from it and a replay does not
 * reproduce it. Seeded from the save on first use so a reload keeps counting
 * where the last session stopped, and cleared with the run.
 */
let clock: { played: number; at: number } | null = null;

/**
 * Fold the time since the last save into the total.
 *
 * Seeded lazily from disk rather than at load, so every path that reaches a
 * save — Continue, a fresh run, a reload nobody expected — starts from what is
 * stored rather than from nought. It costs one read per session.
 */
function tick(now: number): number {
  if (!clock) clock = { played: read()?.played ?? 0, at: now };
  clock.played += Math.min(Math.max(0, now - clock.at), IDLE_MS);
  clock.at = now;
  return clock.played;
}

const storage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    // Private browsing, a blocked origin, or no DOM at all. A run that cannot
    // be saved is still a run worth playing.
    return null;
  }
};

/** Said once a session, when a run is saved without its history. See `saveRun`. */
let warnedNoLog = false;

/** Write the run. A few tens of KB, so it is cheap enough to call per command. */
export function saveRun(
  state: GameState,
  level: string,
  difficulty?: Level,
  /** The store's log, for undo and history after a reload (§29 U5). */
  log?: readonly Command[]
): void {
  const store = storage();
  if (!store) return;
  try {
    const save: StoredRun = {
      format: FORMAT,
      state: encode(state),
      hash: hashState(state),
      level,
      plies: state.history.length,
      difficulty,
      saved: Date.now(),
      played: tick(Date.now())
    };
    const carried = selfContained(log);
    try {
      store.setItem(KEY, JSON.stringify(carried ? { ...save, log: carried } : save));
    } catch {
      // **Quota, and the log is the part worth dropping.** It is the larger
      // half and the one the run does not depend on — a snapshot-only save is
      // exactly what every save was before §29 U5 — so the run survives and
      // only its history does not. Said in the console once, because this
      // happens per command and would otherwise be a wall.
      store.setItem(KEY, JSON.stringify(save));
      if (!warnedNoLog) {
        warnedNoLog = true;
        console.warn("Run saved without its history: storage is full.");
      }
    }
  } catch {
    // Losing the save is bad; taking the game down with it is worse, and the
    // player is mid-run.
  }
}

function read(): StoredRun | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(KEY);
    if (!raw) return null;
    return migrate(JSON.parse(raw) as StoredRun);
  } catch {
    return null;
  }
}

/** Enough to describe the save on a card, without paying to decode it. */
export function savedRun(): SaveSummary | null {
  const save = read();
  return (
    save && {
      level: save.level,
      plies: save.plies,
      difficulty: save.difficulty,
      saved: save.saved,
      played: save.played
    }
  );
}

export function clearRun(): void {
  storage()?.removeItem(KEY);
  // Or the next run inherits the last one's hours. Starting over is the one
  // moment the total is genuinely nought, and the only place that can be said.
  clock = null;
}

export type LoadFailure = "no-save" | "broken";

/**
 * Carry a decoded run's card ids forward onto the names the deck uses today.
 *
 * **In place, on the decoded snapshot, after the hash check** — see `loadRun`
 * for why that order is the only correct one.
 *
 * Both lists, because both are hashed and only one is obvious: `deck` is what
 * the player is holding and `spent` is what they have played, which the round
 * card and the scorecard read back by id. `hand` needs nothing — it holds
 * *instance* numbers pointing into the deck, not card ids.
 *
 * **An id nothing resolves is left exactly as it was.** This aliases; it does
 * not audit. A card that is genuinely gone is a different decision with a
 * different answer (`newGame` drops it), and quietly reaching into somebody's
 * save to delete a prize is not something to do as a side effect of a rename.
 */
function migrateCards(state: GameState): void {
  const run = state.run;
  if (!run) return;
  for (const entry of run.deck) {
    const now = CARD_ALIASES[entry.card];
    if (now) entry.card = now;
  }
  for (let i = 0; i < run.spent.length; i += 1) {
    const now = CARD_ALIASES[run.spent[i]];
    if (now) run.spent[i] = now;
  }
}

/** A run off the disk. `log` is present only when it was saved *and* verified. */
export interface LoadedRun {
  state: GameState;
  difficulty?: Level;
  /** Verified: replay it into the store and history and undo survive the reload. */
  log?: readonly Command[];
  /**
   * A log was saved and no longer reproduces the board, so it was dropped.
   *
   * **Distinct from having no log at all**, which every save written before
   * §29 U5 has: that one changed nothing and says nothing. This one is the
   * single case the notice is for, and the caller says it once.
   */
  historyDropped: boolean;
}

/**
 * Decode a stored snapshot, check it against its hash and rebuild what is
 * derived — or null when it is not the state that was written. `loadRun`'s
 * half that is not about runs, shared with the dev refresh (`devResume.ts`).
 */
export function reviveSnapshot(encoded: unknown, hash: number): GameState | null {
  const state = decode(encoded) as GameState;
  // §31 A6: a save from before Artisan frailty has none — empty, as hashed.
  state.position.frailty ??= [];
  state.position.wards ??= [];
  state.position.artisans ??= [];
  state.position.brass ??= [];
  if (hashState(state) !== hash) return null;
  // The position's structure-owner mirror is derived state, so it is rebuilt
  // rather than trusted: a save written before it existed decodes to a
  // position without the map at all (§26 F5). After the hash check, which
  // never reads the mirror — the industry's own record is what is hashed.
  rebuildStructureMirror(state.position, state.industry);
  // And the artifact mirror (§31 A6, A9), derived from gear and structures.
  syncArtifactMirror(state.position, state.industry);
  // Piece ids likewise: unhashed, so absent from an older save rather than broken.
  restorePieceIds(state.position);
  // **Migrate after the check, never before.** The card id is in the hash
  // (`hash.ts`, `run.deck` as `id:card`), so the ids this save was written
  // with are the ones it was hashed with — rewriting them first would fail
  // the corruption check on every renamed card and throw the run away for
  // being *correct*. Verify what was written, then carry it forward: the
  // same order `migrateWeights` takes with a stored champion.
  migrateCards(state);
  return state;
}

/**
 * Decode the save, and check it is the state that was written.
 *
 * The hash is the same determinism canary two networked clients use, pointed
 * at storage: it catches a save that decoded into something structurally
 * plausible and semantically wrong — a container that came back as a plain
 * object, a typed array that lost its type — which is exactly the failure a
 * snapshot format is prone to and exactly the one that would otherwise show up
 * as a board that is quietly incorrect.
 */
export function loadRun(): LoadedRun | { failed: LoadFailure } {
  const save = read();
  if (!save) return { failed: "no-save" };

  try {
    const state = reviveSnapshot(save.state, save.hash);
    if (!state) return { failed: "broken" };
    // **Pick the clock up where it was left**, and start the next interval from
    // now rather than from when the save was written — the hours between
    // closing the tab and pressing Continue are not time spent in a run.
    clock = { played: save.played ?? 0, at: Date.now() };
    // **The log is checked against the hash as written**, before `migrateCards`
    // has any bearing on either side. A deck that needed aliasing drops its
    // history because the log cannot replay it — `grant-card` refuses an id
    // not in `CARDS` and `newGame` drops one — not because of which hash is
    // compared.
    const kept = save.log && verifies(save.log, save.hash) ? save.log : undefined;
    return {
      state,
      difficulty: save.difficulty,
      log: kept,
      historyDropped: save.log !== undefined && kept === undefined
    };
  } catch {
    return { failed: "broken" };
  }
}
